import moment from 'moment';
import Fuse from 'fuse.js';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { EJSON } from 'meteor/ejson';
import { DateTime } from 'luxon';
import { stringify } from 'csv-stringify/sync';
import { dateTimeFormatOptions } from '/imports/options';
import { Dict, Docmnt, RefMap, ModelClass } from '/imports/types';
import { http } from '/imports/http';
import { geoIp } from '/imports/api/miscl';

/** Escape a user-typed string for literal use inside a RegExp. */
export const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const environments = {
  development: 'http://localhost:3000',
  staging: 'http://localhost:3000',
  production: 'http://localhost:3000',
};

export const randomString = (n = 8) => {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < n; i++) result += characters.charAt(Math.floor(Math.random() * charactersLength));
  return result;
};

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const addError = (errors: { [key: string]: string[] }, key: string, error: string) => {
  errors[key] = errors[key] || [];
  errors[key].push(error);
  return false;
};

export const pluck = <T extends object, K extends (keyof T)>(obj: T, keys: K[]) => {
  const plucked: any = {};
  for (const key of keys) {
    if (key in obj) plucked[key] = obj[key];
  }
  return plucked as Pick<T, K>;
};

export const setProp = (obj: any, key: string, value: any) => {
  obj[key] = value;
};

export const objectKeys = (obj: any) => {
  return Object.keys(obj);
};

export const numKeys = (obj: any) => {
  return objectKeys(obj).length;
};

export const objectValues = <T>(obj: { [key: string]: T }) => {
  return Object.values(obj);
};

export const objectEntries = <T>(obj: { [key: string]: T }) => {
  return Object.entries(obj);
};

export const objectAssign = <T extends object, U>(obj1: T, obj2: U) => {
  return Object.assign(obj1, obj2);
};

export const isPrimitive = (val: any) => {
  if (val === null) return true;
  if (typeof val == 'object' || typeof val == 'function') return false;
  else return true;
};

export const dateToInputString = (date: Date) => {
  const tzDate = new Date(date.getTime());
  tzDate.setMinutes(tzDate.getMinutes() - date.getTimezoneOffset());
  return tzDate.toISOString().substring(0, 16);
};

export const defer = (func: () => void) => {
  setTimeout(func, 0);
};

export const deferUntil = (func: () => void, cond: () => boolean | undefined, interval = 10, timeout = 3000) => {
  const start = Date.now();
  let deferring = false;
  const _interval = setInterval(() => {
    if (Date.now() - start > timeout) clearInterval(_interval);
    if (deferring) return;
    deferring = true;
    if (cond()) {
      clearInterval(_interval);
      func();
    }
    deferring = false;
  }, interval);
  setTimeout(func, 0);
};

export const formatDateTime = (date?: Date, format?: string, zone?: string) => {
  if (!date) return '';
  if (!format) format = Meteor.user()?.dateTimeFormat || dateTimeFormatOptions()[0].value;
  if (!format) format = dateTimeFormatOptions()[0].value;
  if (!zone) zone = Meteor.user()?.timezone;
  return DateTime.fromJSDate(date, { zone }).toFormat(format);
};

export const formatDate = (date?: Date, format?: string, zone?: string) => {
  if (!date) return '';
  if (!format) format = Meteor.user()?.dateTimeFormat || dateTimeFormatOptions()[0].value;
  if (!format) format = dateTimeFormatOptions()[0].value;
  format = dateFormat(format);
  if (!zone) zone = Meteor.user()?.timezone;
  return DateTime.fromJSDate(date, { zone }).toFormat(format);
};

export const formatTime = (date: Date, format?: string, zone?: string) => {
  if (!format) format = Meteor.user()?.dateTimeFormat || dateTimeFormatOptions()[0].value;
  if (!format) format = dateTimeFormatOptions()[0].value;
  format = timeFormat(format);
  if (!zone) zone = Meteor.user()?.timezone;
  return DateTime.fromJSDate(date, { zone }).toFormat(format);
};

export const dateFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'LL/dd/yyyy',
    'LL/dd/yyyy HH:mm': 'LL/dd/yyyy',
    'dd/LL/yyyy h:mm a': 'dd/LL/yyyy',
    'dd/LL/yyyy HH:mm': 'dd/LL/yyyy',
    'dd/LL/yyyy HH \'h\' mm': 'dd/LL/yyyy',
    'yyyy-LL-dd h:mm a': 'yyyy-LL-dd',
    'yyyy-LL-dd HH:mm': 'yyyy-LL-dd',
    'yyyy-LL-dd HH \'h\' mm': 'yyyy-LL-dd',
  };
  return mapping[dateTimeFormat] || 'LL/dd/yyyy';
};

export const timeFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'h:mm a',
    'LL/dd/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy h:mm a': 'h:mm a',
    'dd/LL/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy HH \'h\' mm': 'HH \'h\' mm',
    'yyyy-LL-dd h:mm a': 'h:mm a',
    'yyyy-LL-dd HH:mm': 'HH:mm',
    'yyyy-LL-dd HH \'h\' mm': 'HH \'h\' mm',
  };
  return mapping[dateTimeFormat] || 'h:mm a';
};

export const momentDateFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'MM/DD/YYYY',
    'LL/dd/yyyy HH:mm': 'MM/DD/YYYY',
    'dd/LL/yyyy h:mm a': 'DD/MM/YYYY',
    'dd/LL/yyyy HH:mm': 'DD/MM/YYYY',
    'dd/LL/yyyy HH \'h\' mm': 'DD/MM/YYYY',
    'yyyy-LL-dd h:mm a': 'YYYY-MM-DD',
    'yyyy-LL-dd HH:mm': 'YYYY-MM-DD',
    'yyyy-LL-dd HH \'h\' mm': 'YYYY-MM-DD',
  };
  return mapping[dateTimeFormat] || 'MM/DD/YYYY';
};

export const momentTimeFormat = (dateTimeFormat: string) => {
  const mapping: { [key: string]: string } = {
    'LL/dd/yyyy h:mm a': 'h:mm A',
    'LL/dd/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy h:mm a': 'h:mm A',
    'dd/LL/yyyy HH:mm': 'HH:mm',
    'dd/LL/yyyy HH \'h\' mm': 'HH [h] mm',
    'yyyy-LL-dd h:mm a': 'h:mm A',
    'yyyy-LL-dd HH:mm': 'HH:mm',
    'yyyy-LL-dd HH \'h\' mm': 'HH [h] mm',
  };
  return mapping[dateTimeFormat] || 'LL/dd/yyyy';
};

export const timeStringToMoment = (value: string | undefined, props?: { includeSeconds?: boolean, includeMillis?: boolean }) => {
  if (!value) return;
  let dateString = `1970-01-01 ${value}`;
  if (!props?.includeSeconds) dateString += ':00';
  if (!props?.includeMillis) dateString += '.000';
  return moment(new Date(dateString));
};

export const todaysDate = () => {
  const d = new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

export const tomorrowsDate = () => {
  const d = new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setDate(d.getDate() + 1);
  return d;
};

export const firstDateOfMonth = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setDate(1);
  return d;
};

export const lastDateOfMonth = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
};

export const firstDateOfWeek = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  const day = d.getDay();
  const firstDay = 0;
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  d.setDate(d.getDate() + firstDay - day);
  return d;
};

export const lastDateOfWeek = (date?: Date) => {
  let d = date && new Date(date.toISOString()) || new Date();
  d = firstDateOfWeek(d);
  d.setDate(d.getDate() + 7);
  return d;
};

export const topOfHour = (date?: Date) => {
  const d = date && new Date(date.toISOString()) || new Date();
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
};

export const meteorCall = <T = any>(method: string, ...args: any[]) => {
  return new Promise<T>((resolve, reject) => {
    Meteor.call(method, ...args, (error: any, result: any) => {
      if (error) reject(error);
      else resolve(result);
    });
  });
};

export const uInt8ToBase64 = (u8Arr: Uint8Array) => {
  const CHUNK_SIZE = 0x8000; //arbitrary number
  const length = u8Arr.length;
  let index = 0;
  let result = '';
  let slice;
  while (index < length) {
    slice = u8Arr.subarray(index, Math.min(index + CHUNK_SIZE, length));
    result += String.fromCharCode.apply(null, Array.from(slice));
    index += CHUNK_SIZE;
  }
  return window.btoa(result);
};

export const dataUrlToFile = (dataurl: string, filename: string) => {
  const arr = dataurl.split(',');
  const mime = arr[0]?.match(/:(.*?);/)?.[1];
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
};

export const formatBytes = (bytes: number, decimals = 0) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const clientDownload = (data: any, filename: string, type: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const clientDownloadUrl = (url: string, filename?: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', filename || url.split('/').at(-1) as string);
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const appendAndClickAnchor = (href: string, props?: any) => {
  const a = document.createElement('a');
  a.href = href;
  for (const p in props || {}) a.setAttribute(p, props?.[p]);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const publicPaths = [
  '/login',
  '/signup',
  '/signup-submitted',
  '/apply',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/terms',
  '/privacy',
  '/google-oauth2-redirect',
  '/discord-oauth2-redirect',
];

export const isPublicPath = (pathname?: string) => {
  pathname = pathname || window.location.pathname;
  return publicPaths.includes(pathname) || pathname.startsWith('/signup') || pathname.startsWith('/r/') || pathname.startsWith('/rc/') || pathname.startsWith('/vp/');
};

export const firstBy = (function () {
  // This was stolen from https://github.com/Teun/thenBy.js
  function identity(v: any) { return v; }

  function ignoreCase(v: any) { return typeof (v) === 'string' ? v.toLowerCase() : v; }

  function makeCompareFunction(f: any, opt: any) {
    opt = typeof (opt) === 'object' ? opt : { direction: opt };
    if (typeof (f) != 'function') {
      const prop = f;
      f = function (v1: any) { return v1[prop] ? v1[prop] : ''; };
    }
    if (f.length === 1) {
      const uf = f;
      const preprocess = opt.ignoreCase ? ignoreCase : identity;
      const cmp = opt.cmp || function (v1: any, v2: any) { return v1 < v2 ? -1 : v1 > v2 ? 1 : 0; };
      f = function (v1: any, v2: any) { return cmp(preprocess(uf(v1)), preprocess(uf(v2))); };
    }
    const descTokens = { '-1': '', desc: '' };
    if (opt.direction in descTokens) return function (v1: any, v2: any) { return -f(v1, v2); };
    return f;
  }

  function tb(this: any, func: any, opt: any) {
    const x = (typeof (this) == 'function' && !this.firstBy) ? this : false;
    const y = makeCompareFunction(func, opt);
    const f = x ? function (a: any, b: any) {
      return x(a, b) || y(a, b);
    }
      : y;
    f.thenBy = tb;
    return f;
  }
  tb.firstBy = tb;
  return tb;
})();

export const uniqueArray = <T>(array: T[]) => {
  return Array.from(new Set(array));
};

export const removeNullish = <T>(array: (T | undefined | null)[]) => {
  return array.filter((a) => a !== null && a !== undefined) as T[];
};

export const cropImageCenter = async (file: File, width: number, height: number) => {
  const reader = new FileReader();
  const image = await new Promise<HTMLImageElement>((resolve) => {
    reader.addEventListener('load', () => {
      const img = new Image();
      img.title = file.name;
      img.src = reader.result as string;
      resolve(img);
    });
    reader.readAsDataURL(file);
  });
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  let sx = 0;
  let sy = 0;
  let sw = image.width;
  let sh = image.height;
  if (image.width > image.height) {
    sx = (image.width - image.height) / 2;
    sw = sh;
  }
  else {
    sy = (image.height - image.width) / 2;
    sh = sw;
  }
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
  return await new Promise<File>((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) return resolve(file);
      const _file = new File([blob], file.name, { type: 'image/jpeg' });
      resolve(_file);
    }, 'image/jpeg');
  });
};

export const passwordRegex = () => {
  return /^(?=.*\d)(?=.*[A-Z])(?!.*[^a-zA-Z0-9])(.{8})$/g;
};

export const dateSuffix = (date: Date) => {
  const digits = `${date.getDate()}`;
  switch (digits) {
    case '1': return 'st';
    case '2': return 'nd';
    case '3': return 'rd';
    case '21': return 'st';
    case '22': return 'nd';
    case '23': return 'rd';
    case '31': return 'st';
    default: return 'th';
  }
};

// export const queryStringToObject = (queryString?: string): Record<string, string | string[]> => {
export const queryStringToObject = (queryString?: string): Record<string, string> => {
  queryString = (queryString || window.location.search || '?').trim();
  if (queryString.startsWith('?')) queryString = queryString.substring(1);
  const params = new URLSearchParams(queryString);
  // const result: Record<string, string | string[]> = {};
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    if (result[key]) return; //result[key] = Array.isArray(result[key]) ? [...result[key], value] : [result[key] as string, value]; 
    else result[key] = value;
  });
  return result;
};

export const stackTrace = () => {
  const obj: any = {};
  Error.captureStackTrace(obj, stackTrace);
  return obj.stack.substr(7);
};

export const zeroPadded = (number: number) => {
  return number > 9 ? `${number}` : `0${number}`;
};

export const mapById = <T>(arr: T[]) => {
  return arr.reduce((map: { [key: string]: T }, doc) => {
    map[String((doc as any)._id)] = doc;
    return map;
  }, {});
};

export const mapByKey = <T>(arr: T[], key: keyof T) => {
  return arr.reduce((map: { [key: string]: T }, doc) => {
    map[doc[key] as string] = doc;
    return map;
  }, {});
};

export const mapArrByKey = <T>(arr: T[], key: keyof T) => {
  return arr.reduce((map: { [key: string]: T[] }, doc) => {
    const v = doc[key] as string;
    if (!map[v]) map[v] = [];
    map[v].push(doc);
    return map;
  }, {});
};

export const isMobile = (userAgent?: string) => {
  userAgent = userAgent || window.navigator.userAgent;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};

type CsvOpts = {
  keys?: string[],
  headerMap?: { [key: string]: string },
  maps?: {
    [key: string]: (value: any, obj: any) => string,
  },
  prepend?: any[][],
  append?: any[][],
};

export const arrayToCsv = (array: any[], opts: CsvOpts = {}) => {
  if (!array.length) return '';
  const keys = opts.keys || Object.keys(array[0]);
  const headerMap = opts.headerMap || {};
  const maps = opts.maps || {};
  const header = keys.map((h) => headerMap[h] || h);
  let data = [header].concat(array.map((obj) => {
    return keys.map((k) => maps[k]?.(obj[k], obj) || obj[k]);
  }));
  if (opts.append) data = data.concat(opts.append);
  return stringify(data);
};

export const capitalize = (string: string) => {
  return string.split(' ').map((word) => `${word.charAt(0).toUpperCase()}${word.substring(1)}`).join(' ');
};

export const timezoneOffset = (date: Date, timeZone: string) => {
  const invdate = new Date(date.toLocaleString('en-US', { timeZone }));
  const diff = date.getTime() - invdate.getTime();
  return diff;
};

export const stringToBase64 = (string: string) => {
  return Meteor.isClient ? window.btoa(string) : Buffer.from(string).toString('base64');
};

export const base64ToString = (base64: string) => {
  return Meteor.isClient ? window.atob(base64) : Buffer.from(base64, 'base64').toString('utf8');
};

// Encode binary (ArrayBuffer) to base64 in chunks — avoids call-stack limits
// from String.fromCharCode(...wholeArray) on large files (e.g. uploaded images).
export const arrayBufferToBase64 = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return Meteor.isClient ? window.btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
};

export const movingAverage = (data: number[], width: number) => {
  const calculateItemsSum = (data: number[], start: number, stop: number) => {
    let sum = 0;
    for (let j = start; j < stop; ++j) sum += data[j];
    return sum;
  };
  const steps = data.length - width;
  const result = [];
  for (let i = 0; i < steps; ++i) {
    const sum = calculateItemsSum(data, i, i + width);
    result.push(sum / width);
  }
  return result;
};

export const sum = (data: number[]) => data.reduce((t, n) => t + n, 0);
export const avg = (data: number[]) => data.reduce((t, n) => t + n, 0)/(data.length || 1);

export const isValidJson = (json: string) => {
  try {
    JSON.parse(json);
    return true;
  }
  catch (_) {
    return false;
  }
};

export const hash = (s: string) => s.split('').reduce((a: number, b: string) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);

export const chartColors = [
  '#6A9BC8', '#448F9C', '#ECAF4B', '#BD2D40', '#109E67', '#E67382', '#663438'
];

export const randomColor = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;

export const isIterable = (obj: any) => {
  return !!obj && typeof obj[Symbol.iterator] === 'function';
};

export const flattenObject = (obj: any, prefix = '') => {
  return Object.keys(obj).reduce((acc: any, k) => {
    const pre = prefix.length ? prefix + '.' : '';
    const v = obj[k];
    if (v && typeof v === 'object') Object.assign(acc, flattenObject(v, pre + k));
    else acc[pre + k] = v;
    return acc;
  }, {});
};

export const unflattenObject = (object: any) => {
  const dottedKeys: string[] = [];
  const structured: any = {};
  const keys = objectKeys(object).sort((a, b) => a.length - b.length);
  for (const k of keys) {
    const parts = k.split('.');
    const lminus1 = parts.length - 1;
    let pointer: any = structured;
    let partsJoined = '';
    for (const [i, p] of parts.entries()) {
      partsJoined += i === 0 ? p : `.${p}`;
      if (dottedKeys.includes(partsJoined) && typeof pointer !== 'object') break;
      ensureProperty(pointer, p, i === lminus1 ? object[k] : {});
      pointer = pointer[p];
    }
  }
  return structured;
};

export const ensureProperty = (object: any, key: string, initial: any) => {
  const value = object[key];
  if (value === undefined) {
    object[key] = initial;
    return initial;
  }
  return value;
};

export const roundNumber = (number: number, decimalPlaces = 0) => {
  const power = 10 ** decimalPlaces;
  return Math.round(power * number) / power;
};

export const formatNumber = (number?: number, decimalPlaces = 0) => {
  // Round the number to the required decimal places
  if (number === undefined) return '-';
  const roundedNumber = roundNumber(number, decimalPlaces);

  // Convert number to string and split into integer and decimal parts
  const string = roundedNumber.toString();
  const [integer, decimal] = string.split('.');

  // Format the integer part with commas
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Pad decimal places with zeros if necessary
  let formattedDecimal = decimal || '';
  while (formattedDecimal.length < decimalPlaces) {
    formattedDecimal += '0';
  }

  // Combine integer and decimal parts
  const formattedNumber = `${formattedInteger}${formattedDecimal.length ? '.' : ''}${formattedDecimal}`;

  return formattedNumber;
};

export const formatPhone = (phone?: string) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const d = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
  if (d.length !== 10) return phone;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export const formatCurrency = (number: number, decimals = 2) => {
  return `$${formatNumber(number, decimals)}`;
};

export const openPrintDialog = (onClose?: () => void) => {
  startPrintMode();
  window.addEventListener('afterprint', () => {
    stopPrintMode();
    onClose?.();
  }, { once: true });
  setTimeout(() => { print(); }, 1000);
};

export const startPrintMode = () => {
  document.querySelectorAll('*').forEach((el) => {
    el.classList.add('print-mode');
  });
};

export const stopPrintMode = () => {
  document.querySelectorAll('*').forEach((el) => {
    el.classList.remove('print-mode');
  });
};

export const urlWithParams = (url: string, params: { [key: string]: string }) => {
  const searchParams = new URLSearchParams(params);
  return `${url}?${searchParams.toString()}`;
};

export const splitDateRange = (fromDate: Date, toDate: Date, interval: number) => {
  const fromDateStamp = fromDate.getTime();
  const toDateStamp = toDate.getTime();
  const dateRanges: { fromDate: Date, toDate: Date }[] = [];
  let cursor = fromDateStamp;
  while (cursor < toDateStamp) {
    const fromDate = new Date(cursor);
    const toStamp = Math.min(cursor + interval, toDateStamp);
    const toDate = new Date(toStamp);
    dateRanges.push({ fromDate, toDate });
    cursor = toDate.getTime();
  }
  return dateRanges;
};

export const stripUndefinedFields = (obj: any) => {
  if (typeof obj === 'object') {
    for (const key in obj) {
      if (obj[key] === undefined) delete obj[key];
    }
  }
  return obj;
};

export const stateCodeMap: { [key: string]: string } = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const consoleLog = (data: any, ...args: any[]) => {
  console.log(`${(new Date()).toISOString()} [console]`, data, ...args);
};

export const sleep = async (millis: number) => {
  await new Promise((resolve) => {
    setTimeout(() => resolve(null), millis);
  });
};

export const retry = async <T>(fn: () => Promise<T>, opts?: { maxAttempts?: number, sleep?: number }): Promise<T> => {
  const maxAttempts = opts?.maxAttempts ?? 3;
  const sleepMs = opts?.sleep ?? 3000;
  for (let attempt = 1; ; attempt++) {
    try {
      return await fn();
    } 
    catch (error) {
      if (attempt >= maxAttempts) throw error;
      await sleep(sleepMs);
    }
  }
};

const allowedOps: Dict<RegExp> = {
  and: /"\$and":/g,
  or: /"\$or":/g,
  exists: /"\$exists":/g,
  in: /"\$in":/g,
  nin: /"\$nin":/g,
  gt: /"\$gt":/g,
  gte: /"\$gte":/g,
  lt: /"\$lt":/g,
  lte: /"\$lte":/g,
  ne: /"\$ne":/g,
  date: /"\$date":/g,
};

export const mongoSanitize = <T>(selector: Mongo.Query<T>) => {
  let str = EJSON.stringify(selector);
  for (const op in allowedOps) {
    const re = allowedOps[op];
    str = str.replace(re, `"#${op}":`);
  }
  str = str.replace(/\$/g, '');
  str = str.replace(/#/g, '$');
  return EJSON.parse(str) as Mongo.Query<T>;
};

export const referenceRender = <T extends Docmnt>(modelClass: ModelClass<T>, key: string) => {
  const relatedClass = modelClass.references?.[key]?.modelClass;
  return (p: T, r: Dict<Dict<Docmnt>>) => {
    if (!p[key]) return '';
    const relatedDoc = r?.[key]?.[p[key] as string];
    if (relatedClass?.asString) return relatedClass?.asString?.(relatedDoc);
    return relatedDoc?.name || relatedDoc?._id || '';
  };
};

export const referenceString = <T extends Docmnt>(modelClass: ModelClass<T>, document: T, key: string) => {
  const relatedClass = modelClass.references?.[key]?.modelClass;
  const value = document[key] as string;
  if (!value) return '';
  const r = document.$references;
  const relatedDoc = r?.[key]?.[value];
  if (relatedClass?.asString) return relatedClass?.asString?.(relatedDoc);
  return relatedDoc?.name || relatedDoc?._id || '';
};

export const range = (n: number) => {
  return [...Array(n).keys()];
};

export const randomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min);
};

export const checkPasswordRules = (password = '') => {
  const hasNumber = /\d/.test(password);
  const hasLetter = /[a-z]/i.test(password);
  const hasSpecialCharacter = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password);
  return hasNumber && hasLetter && hasSpecialCharacter;
};

const emailRegex = /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

export const validateEmail = (email = '') => {
  return emailRegex.test(email);
};

export const isValidEmail = (email?: string) => {
  // horked from npm email-validator
  if (!email) return false;
  const emailParts = email.split('@');
  if (emailParts.length !== 2) return false;
  const account = emailParts[0];
  const address = emailParts[1];
  if (account.length > 64) return false;
  else if (address.length > 255) return false;
  const domainParts = address.split('.');
  if (domainParts.some((part) => {
    return part.length > 63;
  })) return false;
  return emailRegex.test(email);
};

export const throttleFunction = (
  maxRequestsPerInterval: number,
  interval: number,
  evenlySpaced = false,
) => {
  if (evenlySpaced) {
    interval = interval / maxRequestsPerInterval;
    maxRequestsPerInterval = 1;
  }
  const queue: Array<() => Promise<void>> = [];
  let lastIntervalStart = 0;
  let numRequestsPerInterval = 0;
  let timeout: NodeJS.Timeout | undefined;
  const dequeue = () => {
    const intervalEnd = lastIntervalStart + interval;
    const now = Date.now();
    if (now < intervalEnd) {
      timeout !== undefined && clearTimeout(timeout);
      timeout = setTimeout(dequeue, intervalEnd - now);
      return;
    }
    lastIntervalStart = now;
    numRequestsPerInterval = 0;
    for (const callback of queue.splice(0, maxRequestsPerInterval)) {
      numRequestsPerInterval++;
      void callback();
    }
    if (queue.length) timeout = setTimeout(dequeue, interval);
    else timeout = undefined;
  };

  return <Return = unknown>(fn: () => Promise<Return> | Return): Promise<Return> => new Promise<Return>(
    (resolve, reject) => {
      const callback = () => Promise.resolve().then(fn).then(resolve).catch(reject);
      const now = Date.now();
      if (timeout === undefined && (now - lastIntervalStart) > interval) {
        lastIntervalStart = now;
        numRequestsPerInterval = 0;
      }
      if (numRequestsPerInterval++ < maxRequestsPerInterval) {
        void callback();
      }
      else {
        queue.push(callback);
        if (timeout === undefined) {
          timeout = setTimeout(dequeue, lastIntervalStart + interval - now);
        }
      }
    },
  );
};

export const deepMerge = (...objs: any[]) => {
  const getType = (obj: any) => {
    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  };
  const mergeObj = (clone: any, obj: any) => {
    for (const [key, value] of objectEntries(obj)) {
      const type = getType(value);
      if (clone[key] !== undefined && getType(clone[key]) === type && ['array', 'object'].includes(type)) {
        clone[key] = deepMerge(clone[key], value);
      } 
      else {
        clone[key] = structuredClone(value);
      }
    }
  };
  let clone = structuredClone(objs.shift());
  for (const obj of objs) {
    const type = getType(obj);
    if (getType(clone) !== type) {
      clone = structuredClone(obj);
      continue;
    }
    if (type === 'array') clone = [...clone, ...structuredClone(obj)];
    else if (type === 'object') mergeObj(clone, obj);
    else clone = obj;
  }
  return clone;
};

export const simulateClick = async (selector: string | HTMLElement, jitter = 0) => {
  const element = typeof selector === 'string' ? document.querySelector(selector) as HTMLElement : selector;
  if (!element) return;
  const box = element.getBoundingClientRect();
  const jitterX = jitter * Math.random() * box.width / 2 * (Math.random() > 0.5 ? 1 : -1);
  const jitterY = jitter * Math.random() * box.height / 2 * (Math.random() > 0.5 ? 1 : -1);
  const coordX = box.left + (box.right - box.left) / 2 + jitterX;
  const coordY = box.top + (box.bottom - box.top) / 2 + jitterY;
  const simulateMouseEvent = (element: HTMLElement, eventName: string, coordX: number, coordY: number) => {
    element.dispatchEvent(new MouseEvent(eventName, {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: coordX,
      clientY: coordY,
      button: 0
    }));
  };
  simulateMouseEvent(element, 'mousedown', coordX, coordY);
  simulateMouseEvent(element, 'mouseup', coordX, coordY);
  simulateMouseEvent(element, 'click', coordX, coordY);
};

export const getIpAddress = async () => {
  const response = await http.get(`//${window.location.host}/clientip`);
  return response.data.ip;
};

export const getGeoInfo = async (ipAddress?: string) => {
  const result = await geoIp.call(ipAddress);
  const origin = { lat: result.location.latitude, lng: result.location.longitude };
  return { result, origin };
};

export const attachReferences = <T extends Docmnt, R extends RefMap<T>>(documents: T[], $references: { [k in keyof R]: Dict<R[k]> }) => {
  if (!$references) return documents;
  const refKeys = objectKeys($references);
  return documents.map((doc) => {
    const _doc = { ...doc, $references: {} };
    for (const key of refKeys) {
      const refId = doc[key] as string;
      const refDoc = $references[key]?.[refId];
      if (!_doc.$references[key]) _doc.$references[key] = {};
      if (refDoc) _doc.$references[key][refId] = refDoc;
    }
    return _doc;
  });
};

export const urlHost = (url: string) => {
  return (new URL(url)).host;
};

export const arrayIntersection = <T>(arr1: T[], arr2: T[]) => {
  return arr1.filter((x) => arr2.includes(x));
};


export const filterBoolean = <T>(array: (T | undefined | null)[]) => {
  return array.filter(Boolean) as T[];
};

export const chunkArray = <T>(array: T[], chunkSize: number) => {
  const res: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
};

export const currentUserId = () => {
  try { return Meteor.userId(); }
  catch (_) { return; }
};

export const readFileAsText = (file: File, encoding?: string) => {
  return new Promise<string | undefined>((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', (e) => resolve(e.target?.result as string));
    reader.readAsText(file, encoding);
  });
};

export const roundDateToMinutes = (minutes: number, date: Date) => {
  const ms = 1000 * 60 * minutes;
  return new Date(Math.round(date.getTime() / ms) * ms);
};

export const numYearsBetween = (date1: Date, date2: Date) => {
  if (date1.getTime() > date2.getTime()) {
    const date = date1;
    date1 = date2;
    date2 = date;
  }
  const startDate = new Date(date1);
  const endDate = new Date(date2);
  let years = endDate.getFullYear() - startDate.getFullYear();
  const monthDiff = endDate.getMonth() - startDate.getMonth();
  const dayDiff = endDate.getDate() - startDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) years--;
  return years;
};

export const fuzzyMatch = (str1: string, str2: string, threshold = 0.3) => {
  const fuse = new Fuse([str2], { includeScore: true, threshold });
  const result = fuse.search(str1);
  return result;
};

export const epsg4326To3857 = (lat4326: number, lng4326: number) => {
  const R = 6378137; // Earth's radius used by EPSG:3857
  const lonRad = (lng4326 * Math.PI) / 180;
  const latRad = (lat4326 * Math.PI) / 180;
  const x = R * lonRad;
  const sinLat = Math.sin(latRad);
  const y = R * Math.log((1 + sinLat) / (1 - sinLat)) / 2;
  return { lat: y, lng: x };
};

export const optionsToDict = (options: { value: string | number, label: string | null }[]) => {
  return options.reduce((m: Dict<string | null>, o) => {
    m[o.value] = o.label;
    return m;
  }, {});
};

export const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

export const cloneObject = <T extends EJSON>(obj: T) => EJSON.parse(EJSON.stringify(obj));